// src/pages/PackOpening.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import { getRandomMuse } from '../../../data/packs';
import { Muse } from '../../../types';
import RarityGem from '../../../shared/ui/RarityGem';
import { playSound } from '../../../utils/audio';
import '../styles/PackOpening.css';

const PackOpening: React.FC = () => {
  const { userData, currentUser, updateUserData } = useAuth();
  const [pulls, setPulls] = useState<Muse[]>([]);
  const [revealed, setRevealed] = useState<boolean>(false);
  const packOpenedRef = useRef<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const runPull = async (): Promise<void> => {
      // Prevent multiple pack openings using ref
      if (packOpenedRef.current) {
        console.log('Pack already opened (ref check), skipping...');
        return;
      }

      const packId = localStorage.getItem('openPack');
      if (!packId) {
        navigate('/app/market');
        return;
      }

      if (!currentUser || !userData) {
        navigate('/app/market');
        return;
      }

      try {
        console.log('Opening pack for the first time...');
        packOpenedRef.current = true; // Set ref immediately

        // Reward-only SFX: pack opening moment
        try {
          playSound('/audio/pack-open.mp3', 0.6);
        } catch (e) {
          console.warn('Failed to play pack-open sound', e);
        }

        // Generate exactly 3 muses and store them in a variable
        const results: Muse[] = Array.from({ length: 3 }, () => getRandomMuse(packId));
        
        // Log what we're about to save for debugging
        console.log('Pack opening - Generated muses:', results.map(m => ({ name: m.name, rarity: m.rarity })));
        
        // Set the pulls immediately for display consistency
        setPulls(results);

        // Optimistically merge pulls into existing muses BEFORE writing to the database
        const current = Array.isArray(userData.muses) ? userData.muses : [];
        updateUserData({ muses: [...current, ...results] });

        // Add all muses to user's collection using smart merging to preserve upgrades
        const { FirebaseService } = await import('../../../shared/storage/FirebaseService');
        await FirebaseService.addMusesToUser(userData.id, results);

        console.log('Pack opening - Successfully saved to Firestore:', results.map(m => ({ name: m.name, rarity: m.rarity })));
        
        localStorage.removeItem('openPack');
        
        // Add reveal animation delay and play rarity-based sounds once cards flip in
        setTimeout(() => {
          setRevealed(true);
          try {
            // Use the highest rarity in the pull to choose a single celebratory sound
            const rarities = results.map(m => (m.rarity || 'Common').toLowerCase());
            const hasLegendary = rarities.includes('legendary');
            const hasEpic = rarities.includes('epic');
            const hasRare = rarities.includes('rare');

            if (hasLegendary) {
              playSound('/audio/triumph.mp3', 0.7);
            } else if (hasEpic) {
              playSound('/audio/fireworks.mp3', 0.6);
            } else if (hasRare) {
              playSound('/audio/badge-drop.mp3', 0.5);
            } else {
              playSound('/audio/sparkle.mp3', 0.4);
            }
          } catch (e) {
            console.warn('Failed to play pack reveal sound', e);
          }
        }, 1000);
      } catch (error) {
        console.error('Error opening pack:', error);
        void window.appAlert('Failed to open pack. Please try again.');
        packOpenedRef.current = false; // Reset on error so user can try again
        navigate('/app/market');
      }
    };
    
    if (userData && currentUser && !packOpenedRef.current) {
      runPull();
    }
  }, [navigate, userData, currentUser]);

  return (
    <div className="pack-container">
      <div className="pack-title">🎉 Your New Muses</div>
      
      <div className="pack-grid">
        {pulls.map((m, i) => (
          <div 
            key={i} 
            className={`pack-card ${m.rarity?.toLowerCase()} ${revealed ? 'revealed' : ''}`}
            style={{ transitionDelay: `${i * 0.2}s` }}
          >
            <div className="pack-card-header">
              <RarityGem 
                rarity={m.rarity} 
                size={32} 
                style={{ marginRight: '12px' }}
              />
              <div className="muse-name">{m.name}</div>
            </div>
            <div className="pack-card-body">
              <div className="muse-image-container">
                <img 
                  src={m.img} 
                  alt={m.name}
                  className="muse-image-large"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pack-buttons">
        <button 
          onClick={() => navigate('/app/market')}
          className="pack-button pack-button-secondary"
        >
          Open More Packs
        </button>
        
        <button 
          onClick={() => navigate('/app/mymuses')}
          className="pack-button pack-button-primary"
        >
          View in My Muses
        </button>
      </div>
    </div>
  );
};

export default PackOpening;
